/**
 * Local-first storage layer.
 *
 * Three tiers based on the selective-persistence model:
 *
 *   - Ephemeral: in-memory only, lost on tab close. Voice/call/draft state.
 *   - Session:   IndexedDB encrypted under session key. Active threads, forms.
 *   - Long-term: IndexedDB encrypted under identity key + OPFS for blobs.
 *                Envelope log, contribution history, file vault metadata.
 *
 * The keyring service worker holds unlocked keys in memory only — they are
 * NEVER written to disk in plaintext. The encrypted blobs on disk are
 * useless without the user's identity unlock.
 */

import Dexie, { type Table } from 'dexie';
import { utf8ToBytes } from '@noble/hashes/utils';

import type { SignedEnvelope } from '../envelope/Envelope';

// ─────────────────────────────────────────────────────────────────────────────
// PERSISTENCE CLASS — declared by each surface, enforced by storage router
// ─────────────────────────────────────────────────────────────────────────────

export type PersistenceClass = 'ephemeral' | 'session' | 'longterm';

export interface SurfacePersistenceDeclaration {
  surfaceId: string;
  surfaceClass: 'transient' | 'persistent' | 'ambient';
  persistence: PersistenceClass;
}

// ─────────────────────────────────────────────────────────────────────────────
// SCHEMAS
// ─────────────────────────────────────────────────────────────────────────────

export interface EnvelopeRecord {
  hash: string;             // primary key
  streamId: string;         // index — which stream this belongs to
  contextEnv: string;       // index — environment for fast filtering
  lamport: number;
  parentHash: string;
  timestamp: string;
  /** AEAD-encrypted SignedEnvelope JSON, ciphertext + nonce + tag. */
  ciphertext: Uint8Array;
}

export interface StreamRecord {
  streamId: string;
  contextEnv: string;
  purpose: string;
  headHash: string;
  headLamport: number;
  memberPubkeys: string[];
  /** Last sync timestamp from peers. */
  lastSyncAt: string;
}

export interface SessionRecord {
  id: string;
  surfaceId: string;
  /** AEAD ciphertext of session blob. */
  ciphertext: Uint8Array;
  /** Auto-expire timestamp. */
  expiresAt: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// DEXIE DATABASE
// ─────────────────────────────────────────────────────────────────────────────

export class MeshDatabase extends Dexie {
  envelopes!: Table<EnvelopeRecord, string>;
  streams!: Table<StreamRecord, string>;
  sessions!: Table<SessionRecord, string>;
  blobs!: Table<BlobRecord, string>;

  // Threads-related tables (added in v2; see schema.ts for canonical types).
  threads_index!: Table<import('./schema').ThreadIndexRecord, string>;
  mls_state!: Table<import('./schema').MlsStateRecord, string>;
  message_cache!: Table<import('./schema').MessageCacheRecord, string>;
  audit_log!: Table<import('./schema').AuditLogRecord, number>;

  // Contacts (added in v3).
  contacts!: Table<import('./schema').ContactRecord, string>;

  // Own KeyPackages (added in v4) — see schema.ts. Lives in the Threads
  // function group: tracks unspent KeyPackages we've generated for ourselves
  // so we can look up the right private bytes when accepting an invite.
  own_key_packages!: Table<import('./schema').OwnKeyPackageRecord, string>;

  // Read-state + mute table (added in v5). Threads group.
  read_state!: Table<import('./schema').ReadStateRecord, string>;
  mute_table!: Table<import('./schema').MuteRecord, string>;

  // Mesh & Discovery tables (added in v5).
  peer_book!: Table<import('./schema').PeerBookRecord, string>;
  invite_ledger!: Table<import('./schema').InviteLedgerRecord, string>;

  // Funnels & Datasets tables (added in v6).
  funnel_definitions!: Table<import('./schema').FunnelDefinitionRecord, string>;
  funnel_drafts!: Table<import('./schema').FunnelDraftRecord, string>;
  own_submissions!: Table<import('./schema').OwnSubmissionRecord, string>;

  // Disaster Recovery tables (added in v7).
  outbound_queue!: Table<import('./schema').OutboundQueueRecord, string>;
  inbound_queue!: Table<import('./schema').InboundQueueRecord, string>;
  recovery_snapshots!: Table<import('./schema').RecoverySnapshotRecord, string>;

  // ALDS Incidents tables (added in v9).
  incident_meta!: Table<import('./schema').IncidentMetaRecord, string>;
  incident_assignments!: Table<import('./schema').IncidentAssignmentRecord, string>;
  roster_status!: Table<import('./schema').RosterStatusRecord, string>;

  // ALDS v2 tables (added in v10): call_history materialized + federation.
  call_history!: Table<import('./schema').CallHistoryRecord, string>;
  federation_links!: Table<import('./schema').FederationLinkRecord, string>;
  federated_incidents!: Table<import('./schema').FederatedIncidentRecord, string>;

  // ALDS v2 Phase 11: Semaphore membership group mirror.
  membership_log!: Table<import('./schema').MembershipLogRecord, string>;

  // ALDS v2 Phase 12: credentials + push subscriptions.
  responder_credentials!: Table<import('./schema').ResponderCredentialRecord, string>;
  push_subscriptions!: Table<import('./schema').PushSubscriptionRecord, string>;

  // ALDS v3 Phase 15: IPFS public bulletin tables.
  pinning_endpoints!: Table<import('./schema').PinningEndpointRecord, string>;
  published_bulletins!: Table<import('./schema').PublishedBulletinRecord, string>;

  // Phase 69.A: news headlines (ticker + news-paper surface).
  news_headlines!: Table<import('./schema').NewsHeadlineRecord, string>;

  // ALDS v3 Phase 17: jurisdiction plugins.
  plugins!: Table<import('./schema').PluginRecord, string>;

  // ALDS v3 Phase 18: secure-enclave key storage attestation.
  key_storage_attestation!: Table<import('./schema').KeyStorageAttestationRecord, string>;

  // ALDS v5 Phase 27.1: social communication surfaces (ADSCS).
  social_groups!: Table<import('./schema').SocialGroupRecord, string>;
  social_subscriptions!: Table<import('./schema').SocialSubscriptionRecord, string>;
  social_mentions!: Table<import('./schema').SocialMentionRecord, string>;

  // ALDS v4 Phase 12.5.x: peer push subscriptions learned via push-advert envelope.
  peer_push_subscriptions!: Table<import('./schema').PeerPushSubscriptionRecord, string>;

  // ALDS v6 Phase 30.4: wakeup retry queue.
  wakeup_retry_queue!: Table<import('./schema').WakeupRetryQueueRecord, string>;

  // ALDS v6 Phase 31.1: active group calls.
  group_calls!: Table<import('./schema').GroupCallRecord, string>;

  // ALDS v7 Phase 37: relay-peer buffer for offline-recipient delivery.
  relay_peer_buffer!: Table<import('./schema').RelayPeerBufferRecord, string>;

  // ALDS v7 Phase 39: anchor log (audit Merkle root → public chain anchors).
  anchor_log!: Table<import('./schema').AnchorLogRecord, string>;

  // Vault manifest cache — read-side index for media manifests (was
  // schema-doc only before; Phase 46 (v9) needs Table-level access).
  manifests_cache!: Table<import('./schema').ManifestCacheRecord, string>;

  // ALDS v9 Phase 47: aggregated federated search results.
  federation_search_results!: Table<import('./schema').FederationSearchResultRecord, string>;

  // ALDS v10 Phase 52 close-out — peer attestations of anchor CIDs.
  cid_attestations!: Table<import('./schema').CidAttestationRecord, string>;

  // ALDS v10 Phase 53 — social key recovery (Shamir M-of-N).
  peer_shares_given!: Table<import('./schema').PeerShareGivenRecord, string>;
  peer_shares_held!: Table<import('./schema').PeerShareHeldRecord, string>;
  recovery_sessions!: Table<import('./schema').RecoverySessionRecord, string>;

  // ALDS v10 Phase 54 — real-time presence.
  presence_status!: Table<import('./schema').PresenceStatusRecord, string>;

  // ALDS v10 Phase 55 — member-vote prepublish moderation.
  pending_posts!: Table<import('./schema').PendingPostRecord, string>;
  pending_post_votes!: Table<import('./schema').PendingPostVoteRecord, string>;

  // ALDS v10 Phase 56 — BLE mesh substrate.
  ble_outbox!: Table<import('./schema').BleOutboxRecord, string>;
  ble_peer_book!: Table<import('./schema').BlePeerRecord, string>;

  // ALDS v10 Phase 57 — SMS gateway.
  sms_subscribers!: Table<import('./schema').SmsSubscriberRecord, string>;
  sms_messages!: Table<import('./schema').SmsMessageRecord, string>;

  constructor(dbName: string = 'surface-mesh-v1') {
    super(dbName);
    this.version(1).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
    });
    // Version 2 adds threads + audit. Dexie auto-migrates existing data.
    this.version(2).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
    });
    // Version 3 adds contacts.
    this.version(3).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
    });
    // Version 4 adds own_key_packages for MLS invite-accept flow.
    this.version(4).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
    });
    // Version 5 adds read_state + mute_table (Threads), peer_book +
    // invite_ledger (Mesh).
    this.version(5).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
    });
    // Version 6 adds funnels.
    this.version(6).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
    });
    // Version 7 adds Disaster Recovery tables: outbound/inbound queues +
    // recovery snapshots. All local-only, never synced.
    this.version(7).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
    });
    // Version 8 adds a standalone `threadId` index on message_cache to
    // support .where('threadId').equals(...) queries used by ThreadInbox
    // control-message handling and several callers.
    this.version(8).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
    });
    // Version 9 adds ALDS incident tables: incident_meta, incident_assignments,
    // roster_status. All local-only (per-device). The thread + funnel-submission
    // they reference carry the synced truth.
    this.version(9).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
    });
    // Version 10 materializes call_history (Phase 8) and adds federation
    // tables (Phase 9). call_history was always declared in schema.ts but
    // wasn't on the Dexie store list until the Voice & Video group came
    // online.
    this.version(10).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
    });
    // Version 11 adds the Semaphore membership group mirror.
    this.version(11).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
    });
    // Version 12 adds Phase 12 field-ops tables: sworn-responder credentials
    // and Web Push subscriptions.
    this.version(12).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
    });

    // Version 13 — Phase 14 multi-hop federation + per-treaty redaction.
    // Pure additive: forwardChain on federated_incidents, hopTtl/redactionRules
    // /autoForward on federation_links. Neither requires new indexes.
    this.version(13).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
    });

    // Version 14 — Phase 15: IPFS public bulletin tables.
    // pinning_endpoints holds operator-configured pin services;
    // published_bulletins records each daily redacted day-log CID.
    this.version(14).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
    });

    // Version 15 — Phase 17 jurisdiction plugins.
    this.version(15).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
    });

    // Version 16 — Phase 18 secure-enclave key storage attestation.
    this.version(16).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
    });

    // Version 17 — Phase 27.1 ADSCS (social communication surfaces).
    this.version(17).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
    });

    // Version 18 — Phase 12.5.x peer push subscription advertising.
    this.version(18).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
    });

    // Version 19 — Phase 30.4 wakeup retry queue.
    this.version(19).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
    });

    // Version 20 — Phase 31.1 group calls.
    this.version(20).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
    });

    // Version 21 — Phase 37 relay-peer buffer.
    this.version(21).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
    });

    // Version 22 — Phase 52 close-out: peer CID attestations.
    this.version(22).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
      cid_attestations: 'id, epoch, cid, observerPubkey, observedAt',
    });

    // Version 23 — Phase 53 social key recovery (Shamir M-of-N peer shares).
    this.version(23).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
      cid_attestations: 'id, epoch, cid, observerPubkey, observedAt',
      peer_shares_given: 'id, recoveryConfigId, peerPubkey, sentAt',
      peer_shares_held: 'id, requesterPubkey, receivedAt',
      recovery_sessions: 'id, role, counterpartyPubkey, status, startedAt',
    });

    // Version 24 — Phase 54 real-time presence.
    this.version(24).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
      cid_attestations: 'id, epoch, cid, observerPubkey, observedAt',
      peer_shares_given: 'id, recoveryConfigId, peerPubkey, sentAt',
      peer_shares_held: 'id, requesterPubkey, receivedAt',
      recovery_sessions: 'id, role, counterpartyPubkey, status, startedAt',
      presence_status: 'pubkey, neighborhoodId, lastHeartbeatAt',
    });

    // Version 25 — Phase 55 prepublish-vote moderation.
    this.version(25).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
      cid_attestations: 'id, epoch, cid, observerPubkey, observedAt',
      peer_shares_given: 'id, recoveryConfigId, peerPubkey, sentAt',
      peer_shares_held: 'id, requesterPubkey, receivedAt',
      recovery_sessions: 'id, role, counterpartyPubkey, status, startedAt',
      presence_status: 'pubkey, neighborhoodId, lastHeartbeatAt',
      pending_posts: 'id, groupId, authorPubkey, status, expiresAt',
      pending_post_votes: 'id, pendingPostId, voterPubkey',
    });

    // Version 26 — Phase 56 BLE mesh substrate.
    this.version(26).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
      cid_attestations: 'id, epoch, cid, observerPubkey, observedAt',
      peer_shares_given: 'id, recoveryConfigId, peerPubkey, sentAt',
      peer_shares_held: 'id, requesterPubkey, receivedAt',
      recovery_sessions: 'id, role, counterpartyPubkey, status, startedAt',
      presence_status: 'pubkey, neighborhoodId, lastHeartbeatAt',
      pending_posts: 'id, groupId, authorPubkey, status, expiresAt',
      pending_post_votes: 'id, pendingPostId, voterPubkey',
      ble_outbox: 'id, streamId, queuedAt, flushedAt',
      ble_peer_book: 'pubkey, deviceId, lastSeenAt',
    });

    // Version 27 — Phase 57 SMS gateway.
    this.version(27).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
      cid_attestations: 'id, epoch, cid, observerPubkey, observedAt',
      peer_shares_given: 'id, recoveryConfigId, peerPubkey, sentAt',
      peer_shares_held: 'id, requesterPubkey, receivedAt',
      recovery_sessions: 'id, role, counterpartyPubkey, status, startedAt',
      presence_status: 'pubkey, neighborhoodId, lastHeartbeatAt',
      pending_posts: 'id, groupId, authorPubkey, status, expiresAt',
      pending_post_votes: 'id, pendingPostId, voterPubkey',
      ble_outbox: 'id, streamId, queuedAt, flushedAt',
      ble_peer_book: 'pubkey, deviceId, lastSeenAt',
      sms_subscribers: 'phoneNumber, neighborhoodId, pairedAldsPubkey, addedAt',
      sms_messages: 'id, phoneNumber, direction, at',
    });
    // Phase 69.A — news headlines (ticker + news-paper surface).
    this.version(28).stores({
      envelopes: 'hash, streamId, contextEnv, lamport, [streamId+lamport]',
      streams: 'streamId, contextEnv, purpose',
      sessions: 'id, surfaceId, expiresAt',
      blobs: 'cid, [groupId+cid]',
      threads_index: 'threadId, context, kind, lastActivityAt, archived',
      mls_state: 'threadId, context, epoch',
      message_cache: 'hash, context, threadId, [context+threadId], senderPubkey, sentAt, kind',
      audit_log: '++seq, category, action, context, occurredAt',
      contacts: 'id, context, pubkey, trustLevel, lastInteractionAt',
      own_key_packages: 'kpHash, context, createdAt, usedAt',
      read_state: 'id, context, threadId',
      mute_table: 'id, context, threadId, muteUntil',
      peer_book: 'peerId, lastSeenAt, trustLevel',
      invite_ledger: 'inviteId, context, issuedAt, redeemedAt',
      funnel_definitions: 'hash, funnelId, [funnelId+version]',
      funnel_drafts: 'id, updatedAt',
      own_submissions: 'submissionId, context, [context+funnelId], submittedAt',
      outbound_queue: 'hash, context, streamId, queuedAt',
      inbound_queue: 'hash, receivedAt, status',
      recovery_snapshots: 'snapshotId, createdAt',
      incident_meta: 'incidentId, context, threadId, proposedSeverity, status, createdAt, [context+status]',
      incident_assignments: 'id, incidentId, responderPubkey, status',
      roster_status: 'pubkey, onShift, lastActive',
      call_history: 'callId, context, threadId, disposition, startedAt',
      federation_links: 'id, localNeighborhoodId, remoteNeighborhoodId, status, createdAt',
      federated_incidents: 'incidentId, sourceNeighborhoodId, forwardedAt, [sourceNeighborhoodId+forwardedAt]',
      membership_log: 'id, neighborhoodId, commitment, addedAt',
      responder_credentials: 'id, responderPubkey, caPubkey, role, issuedAt',
      push_subscriptions: 'endpoint, subscribedAt',
      pinning_endpoints: 'id, authKind, addedAt',
      published_bulletins: 'cid, endpointId, windowStart, publishedAt',
      news_headlines: 'id, source, publishedAt, dismissedAt',
      plugins: 'id, status, version, installedAt',
      key_storage_attestation: 'id, deviceId, storageKind, attestedAt',
      social_groups: 'id, kind, presentationStyle, visibility, createdAt, archivedAt',
      social_subscriptions: 'id, ownPubkey, groupId, joinedAt, leftAt, [ownPubkey+groupId]',
      social_mentions: 'id, ownPubkey, messageHash, groupId, receivedAt, readAt',
      peer_push_subscriptions: 'id, peerPubkey, endpoint, threadId, receivedAt',
      wakeup_retry_queue: 'id, targetPubkey, attempts, nextRetryAt',
      group_calls: 'id, threadId, status, createdAt',
      relay_peer_buffer: 'id, recipientPubkey, threadId, envelopeHash, bufferedAt',
      anchor_log: 'id, chain, epoch',
      manifests_cache: 'manifestId, [context+groupId], senderPubkey, createdAt, chunksLocal, tombstoned',
      federation_search_results: 'id, queryId, receivedAt',
      cid_attestations: 'id, epoch, cid, observerPubkey, observedAt',
      peer_shares_given: 'id, recoveryConfigId, peerPubkey, sentAt',
      peer_shares_held: 'id, requesterPubkey, receivedAt',
      recovery_sessions: 'id, role, counterpartyPubkey, status, startedAt',
      presence_status: 'pubkey, neighborhoodId, lastHeartbeatAt',
      pending_posts: 'id, groupId, authorPubkey, status, expiresAt',
      pending_post_votes: 'id, pendingPostId, voterPubkey',
      ble_outbox: 'id, streamId, queuedAt, flushedAt',
      ble_peer_book: 'pubkey, deviceId, lastSeenAt',
      sms_subscribers: 'phoneNumber, neighborhoodId, pairedAldsPubkey, addedAt',
      sms_messages: 'id, phoneNumber, direction, at',
    });
  }
}

export interface BlobRecord {
  cid: string;             // content-addressed primary key
  groupId: string;         // which group's vault this belongs to
  /** AEAD-encrypted blob ciphertext + nonce + tag. */
  ciphertext: Uint8Array;
}

// ─────────────────────────────────────────────────────────────────────────────
// AT-REST ENCRYPTION
// ─────────────────────────────────────────────────────────────────────────────
// AES-256-GCM via Web Crypto. Key is derived from identity material in the
// keyring SW; this module never sees the seed phrase.

export interface StorageKey {
  readonly key: CryptoKey;
  /** Domain-separator. Different domains MUST use different keys. */
  readonly domain: 'envelope' | 'session' | 'blob';
}

export async function encryptAtRest(key: StorageKey, plaintext: Uint8Array): Promise<Uint8Array> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as BufferSource }, key.key, plaintext as BufferSource),
  );
  // Prepend 12-byte nonce so we can extract it on decrypt.
  const out = new Uint8Array(12 + ct.length);
  out.set(nonce, 0);
  out.set(ct, 12);
  return out;
}

export async function decryptAtRest(key: StorageKey, blob: Uint8Array): Promise<Uint8Array> {
  const nonce = blob.slice(0, 12);
  const ct = blob.slice(12);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, key.key, ct);
  return new Uint8Array(pt);
}

// ─────────────────────────────────────────────────────────────────────────────
// STORAGE ROUTER
// ─────────────────────────────────────────────────────────────────────────────
// The router is what enforces selective persistence. A surface declares its
// class and persistence policy; the router refuses cross-class writes.

export class StorageRouter {
  #db: MeshDatabase;
  #envelopeKey: StorageKey | null = null;
  #sessionKey: StorageKey | null = null;
  #ephemeralStore = new Map<string, unknown>();

  constructor(db: MeshDatabase) {
    this.#db = db;
  }

  setKeys(envelope: StorageKey, session: StorageKey): void {
    this.#envelopeKey = envelope;
    this.#sessionKey = session;
  }

  async putEnvelope(streamId: string, envelope: SignedEnvelope): Promise<void> {
    if (!this.#envelopeKey) throw new Error('Storage locked');
    const json = utf8ToBytes(JSON.stringify(envelope));
    const ct = await encryptAtRest(this.#envelopeKey, json);
    await this.#db.envelopes.put({
      hash: envelope.hash,
      streamId,
      contextEnv: envelope.header.context.environment,
      lamport: envelope.header.lamport,
      parentHash: envelope.header.parentHash,
      timestamp: envelope.header.timestamp,
      ciphertext: ct,
    });
  }

  async getStream(streamId: string): Promise<SignedEnvelope[]> {
    if (!this.#envelopeKey) throw new Error('Storage locked');
    const records = await this.#db.envelopes
      .where('[streamId+lamport]')
      .between([streamId, Dexie.minKey], [streamId, Dexie.maxKey])
      .toArray();
    const out: SignedEnvelope[] = [];
    for (const r of records) {
      const pt = await decryptAtRest(this.#envelopeKey, r.ciphertext);
      out.push(JSON.parse(new TextDecoder().decode(pt)) as SignedEnvelope);
    }
    return out;
  }

  putEphemeral(key: string, value: unknown): void {
    this.#ephemeralStore.set(key, value);
  }

  getEphemeral<T = unknown>(key: string): T | undefined {
    return this.#ephemeralStore.get(key) as T | undefined;
  }

  /**
   * Store an encrypted blob (file chunk) under a content-addressed key.
   * The blob's content is assumed already encrypted by the file vault layer
   * using a group-derived key — this adds a second layer of at-rest
   * encryption under the session key so disk inspection without unlock
   * reveals nothing.
   */
  async putBlob(groupId: string, cid: string, ciphertext: Uint8Array): Promise<void> {
    if (!this.#sessionKey) throw new Error('Storage locked');
    const wrapped = await encryptAtRest(this.#sessionKey, ciphertext);
    await this.#db.blobs.put({ cid, groupId, ciphertext: wrapped });
  }

  async getBlob(groupId: string, cid: string): Promise<Uint8Array | null> {
    if (!this.#sessionKey) throw new Error('Storage locked');
    const record = await this.#db.blobs.where({ groupId, cid }).first();
    if (!record) return null;
    return decryptAtRest(this.#sessionKey, record.ciphertext);
  }

  async deleteBlob(groupId: string, cid: string): Promise<void> {
    await this.#db.blobs.where({ groupId, cid }).delete();
  }

  // ─────────────────────────────────────────────────────────────────────
  // GENERIC ENCRYPT / DECRYPT FOR HIGHER-LEVEL MODULES
  // ─────────────────────────────────────────────────────────────────────
  //
  // Higher-level modules (ThreadManager, future Vault, etc.) need to
  // encrypt arbitrary bytes under the session key before putting them
  // into Dexie tables they own. Rather than each module wiring up its
  // own AEAD code, they call these methods. The encryption key never
  // leaves the StorageRouter — modules only see ciphertext + plaintext.

  /** Encrypt bytes under the session key for at-rest storage. */
  async encryptForSession(plaintext: Uint8Array): Promise<Uint8Array> {
    if (!this.#sessionKey) throw new Error('Storage locked');
    return encryptAtRest(this.#sessionKey, plaintext);
  }

  /** Decrypt bytes previously written by encryptForSession. */
  async decryptForSession(ciphertext: Uint8Array): Promise<Uint8Array> {
    if (!this.#sessionKey) throw new Error('Storage locked');
    return decryptAtRest(this.#sessionKey, ciphertext);
  }

  /** Read-only accessor for higher-level modules that need direct
   * Dexie access to tables they own (with the understanding that
   * encrypted fields must be written via encryptForSession). */
  get db(): MeshDatabase {
    return this.#db;
  }

  /** Whether storage has live keys and can encrypt/decrypt. */
  isUnlocked(): boolean {
    return this.#sessionKey !== null && this.#envelopeKey !== null;
  }

  /** Burn all ephemeral state and forget cached keys. Called on app lock. */
  async lock(): Promise<void> {
    this.#ephemeralStore.clear();
    this.#envelopeKey = null;
    this.#sessionKey = null;
  }
}
